// «Aggiungi provider» (tela, artboard 14 e 21): si sceglie la tipologia, poi ognuna si collega a
// modo suo. Locale (SurgeAI) chiede l'indirizzo e un abbinamento: PopTown mostra il codice, la
// persona lo approva in SurgeAI › Impostazioni › Assistenti, la chiave arriva da sola. Codex
// mostra link e codice dispositivo; Claude Code chiede il token di «claude setup-token», che
// PopTown prova e non restituisce più; Antigravity (Gemini) si collega con l'account Google o con una
// chiave API (GeminiLogin). Più account dello stesso CLI convivono: ognuno ha un nome (tela,
// artboard 50).
import { useEffect, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import type { PendingProvider, ProviderKind } from '../../lib/api';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Field, TextField } from '../../components/Field';
import { Modal } from '../../components/Modal';
import { GeminiChoice, GooglePending, type GeminiMode } from './GeminiLogin';

const POLL_MS = 3000;

function message(e: unknown, fallback: string): string {
  return e instanceof ApiError && e.detail ? e.detail : fallback;
}

export function AddProvider({
  kinds,
  resume,
  onClose,
}: {
  kinds: ProviderKind[];
  resume?: PendingProvider;
  onClose: () => void;
}) {
  const [kind, setKind] = useState(resume?.kind ?? kinds.find((k) => k.enabled)?.id ?? 'local');
  const method = kinds.find((k) => k.id === kind)?.method ?? 'pairing';
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [token, setToken] = useState('');
  const [geminiMode, setGeminiMode] = useState<GeminiMode>('google');
  const [code, setCode] = useState('');
  // Antigravity con la chiave si collega come Claude Code: con un token provato subito
  const byToken = method === 'token' || (method === 'google' && geminiMode === 'key');
  const [pending, setPending] = useState<PendingProvider | null>(resume ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  // finché l'abbinamento aspetta, si chiede a PopTown ogni 3 secondi se è stato approvato
  useEffect(() => {
    if (!pending || done || pending.needsCode) return;
    const timer = window.setInterval(() => {
      api
        .pollProvider(pending.id)
        .then((r) => {
          if (r.status === 'connected')
            setDone(`${r.provider.name}: ${r.provider.models.length} modelli.`);
          else if (r.status === 'refused') {
            setPending(null);
            setError('Richiesta rifiutata in SurgeAI.');
          } else if (r.status === 'expired') {
            setPending(null);
            setError(
              pending.kind === 'codex'
                ? 'Il codice è scaduto o il login non è riuscito: ricomincia.'
                : 'La richiesta è scaduta: ricomincia.',
            );
          }
        })
        .catch((e: unknown) => {
          setPending(null);
          setError(message(e, 'Abbinamento non riuscito.'));
        });
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [pending, done]);

  const connect = async () => {
    setBusy(true);
    setError(null);
    try {
      if (byToken) {
        const r = await api.addProviderToken(kind, token, name.trim());
        if (r.status === 'connected') setDone(`${r.provider.name}: login riuscito.`);
      } else {
        setPending(
          await api.addProvider(kind, method === 'pairing' ? url : '', name.trim() || undefined),
        );
      }
    } catch (e) {
      setError(message(e, 'Non riesco a contattare il provider.'));
    } finally {
      setBusy(false);
    }
  };

  const sendCode = async () => {
    if (!pending) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.submitProviderCode(pending.id, code);
      if (r.status === 'connected') setDone(`${r.provider.name}: login riuscito.`);
      else {
        setPending(null);
        setCode('');
        // il motivo che dà il CLI (es. Google rifiuta il codice), se c'è
        setError(
          `Il codice non è valido o è scaduto${'error' in r && r.error ? ` (${r.error})` : ''}: ricomincia.`,
        );
      }
    } catch (e) {
      setPending(null);
      setError(message(e, 'Login non riuscito.'));
    } finally {
      setBusy(false);
    }
  };

  const footer = done ? (
    <Button variant="primary" onClick={onClose}>
      Fatto
    </Button>
  ) : (
    <>
      <Button variant="secondary" onClick={onClose}>
        {pending ? 'Chiudi' : 'Annulla'}
      </Button>
      {!pending && (
        <Button
          variant="primary"
          loading={busy}
          disabled={method === 'pairing' ? !url.trim() : byToken && !token.trim()}
          onClick={() => void connect()}
        >
          Collega
        </Button>
      )}
      {pending?.needsCode && (
        <Button
          variant="primary"
          loading={busy}
          disabled={!code.trim()}
          onClick={() => void sendCode()}
        >
          Conferma il codice
        </Button>
      )}
    </>
  );

  return (
    <Modal title="Aggiungi provider" width={520} onClose={onClose} footer={footer}>
      {done ? (
        <Banner kind="info">Collegato. {done}</Banner>
      ) : (
        <>
          <div className="t-small dim" style={{ marginBottom: 12 }}>
            Scegli la tipologia: ognuna si collega a modo suo.
          </div>
          <div
            role="radiogroup"
            aria-label="Tipologia"
            style={{ display: 'grid', gap: 8, marginBottom: 16 }}
          >
            {kinds.map((k) => {
              const on = k.id === kind;
              return (
                <button
                  key={k.id}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  disabled={!k.enabled || !!pending}
                  onClick={() => {
                    setKind(k.id);
                    setError(null);
                  }}
                  style={{
                    textAlign: 'left',
                    border: on ? '2px solid var(--accent)' : '1px solid var(--border)',
                    borderRadius: 'var(--r-lg)',
                    background: 'var(--panel)',
                    color: 'var(--text)',
                    padding: '10px 12px',
                    opacity: k.enabled ? 1 : 0.55,
                    cursor: k.enabled ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 12,
                  }}
                >
                  <span>
                    <span className="t-body" style={{ display: 'block', fontWeight: 600 }}>
                      {k.label}
                    </span>
                    <span className="t-small dim">{k.hint}</span>
                  </span>
                  {!k.enabled && (
                    <span
                      className="t-caption dim"
                      style={{
                        background: 'var(--neutral-soft)',
                        padding: '2px 7px',
                        borderRadius: 'var(--r-full)',
                      }}
                    >
                      In arrivo
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          {!pending && method !== 'pairing' && (
            <div style={{ marginBottom: 12 }}>
              <Field
                label="Nome dell’account"
                hint="Per distinguerlo da altri account dello stesso tipo, es. «Claude lavoro». Facoltativo."
              >
                <TextField value={name} onChange={setName} placeholder="es. Claude lavoro" />
              </Field>
            </div>
          )}
          {!pending && method === 'token' && (
            <>
              <div className="t-small dim" style={{ marginBottom: 10 }}>
                Sul tuo computer, nel terminale: <code>claude setup-token</code>. Accedi con
                l&apos;account che PopTown deve usare e incolla qui il token: PopTown lo prova con
                una domanda breve e non lo mostra più.
              </div>
              <Field label="Token">
                <TextField
                  type="password"
                  value={token}
                  onChange={setToken}
                  placeholder="sk-ant-oat01-…"
                  autoComplete="off"
                  mono
                  autoFocus
                />
              </Field>
            </>
          )}
          {!pending && method === 'google' && (
            <GeminiChoice
              mode={geminiMode}
              onMode={setGeminiMode}
              token={token}
              onToken={setToken}
            />
          )}
          {!pending && method === 'device' && (
            <div className="t-small dim">
              Premi «Collega»: PopTown chiede a Codex un codice da inserire nel browser, con
              l&apos;account ChatGPT che deve usare.
            </div>
          )}
          {!pending && method === 'pairing' && (
            <Field label="Indirizzo">
              <TextField
                value={url}
                onChange={setUrl}
                placeholder="https://ai.ihome.ovh"
                mono
                autoFocus
              />
            </Field>
          )}
          {pending?.needsCode && (
            <div
              style={{
                border: '1px solid var(--border)',
                borderRadius: 'var(--r-lg)',
                background: 'var(--panel-2)',
                padding: 14,
              }}
            >
              <GooglePending pending={pending} code={code} onCode={setCode} />
            </div>
          )}
          {pending && !pending.needsCode && (
            <div
              style={{
                border: '1px solid var(--border)',
                borderRadius: 'var(--r-lg)',
                background: 'var(--panel-2)',
                padding: 14,
              }}
            >
              <div className="t-body" style={{ fontWeight: 600, marginBottom: 6 }}>
                {pending.kind === 'codex' ? 'In attesa del login' : 'In attesa di approvazione'}
              </div>
              {pending.kind === 'codex' ? (
                <div className="t-small dim" style={{ marginBottom: 10 }}>
                  Apri{' '}
                  <a href={pending.url} target="_blank" rel="noreferrer noopener">
                    {pending.url.replace(/^https:\/\//, '')}
                  </a>
                  , accedi con l&apos;account ChatGPT che PopTown deve usare e inserisci il codice.
                  Scade in 15 minuti.
                </div>
              ) : (
                <div className="t-small dim" style={{ marginBottom: 10 }}>
                  Apri SurgeAI › Impostazioni › Assistenti ({pending.url}), controlla il codice e
                  premi «Approva per i modelli».
                </div>
              )}
              <div
                style={{
                  font: '700 26px/32px var(--font-mono)',
                  letterSpacing: '.12em',
                  textAlign: 'center',
                }}
              >
                {pending.userCode}
              </div>
            </div>
          )}
          {error && (
            <div style={{ marginTop: 12 }}>
              <Banner kind="danger">{error}</Banner>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
